'use client';

import { useState } from 'react';


import Icon from '@/components/ui/AppIcon';

interface Episode {
  id: number;
  title: string;
  description: string;
  duration: string;
  date: string;
  series: string;
  topic: string;
  guestType: string;
  image: string;
  alt: string;
  featured?: boolean;
  trending?: boolean;
  plays: string;
}

const episodes: Episode[] = [
{
  id: 1,
  title: 'On the Front Lines: A Ranger\'s Story',
  description: 'Join veteran ranger James Mokoena as he shares two decades of protecting rhinos in Kruger National Park, from night patrols to emotional rescues.',
  duration: '52 min',
  date: 'Mar 5, 2026',
  series: 'Field Voices',
  topic: 'Field Stories',
  guestType: 'Ranger',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1c91d2ced-1767042319677.png",
  alt: 'Conservation ranger in khaki uniform standing with binoculars in African savanna at golden hour',
  featured: true,
  plays: '14.2K'
},
{
  id: 2,
  title: 'The Science of Rhino DNA Tracking',
  description: 'Dr. Sarah Chen explains how cutting-edge genetic analysis is revolutionizing anti-poaching efforts and helping identify trafficking networks.',
  duration: '44 min',
  date: 'Feb 28, 2026',
  series: 'Expert Series',
  topic: 'Science & Research',
  guestType: 'Scientist',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_15fb1fd9d-1772099949713.png",
  alt: 'Scientist in white lab coat examining DNA samples under microscope in modern research laboratory',
  trending: true,
  plays: '9.8K'
},
{
  id: 3,
  title: 'Sounds of the Savanna: Field Recordings',
  description: 'An immersive audio journey through the Hluhluwe-iMfolozi Park — rhino calls, bird songs, and the sounds of a living ecosystem at dawn.',
  duration: '28 min',
  date: 'Feb 20, 2026',
  series: 'Field Recordings',
  topic: 'Field Stories',
  guestType: 'Field Recorder',
  image: "https://images.unsplash.com/photo-1698308956410-0d7d390dfed2",
  alt: 'White rhinoceros mother and calf walking through tall golden grass in African national park',
  plays: '7.3K'
},
{
  id: 4,
  title: 'Community Conservation: Voices from the Village',
  description: 'Local community leaders from Zimbabwe discuss how conservation partnerships have transformed their relationship with wildlife and improved livelihoods.',
  duration: '61 min',
  date: 'Feb 14, 2026',
  series: 'Community Voices',
  topic: 'Community',
  guestType: 'Community Leader',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_12723779a-1772289268641.png",
  alt: 'African village community gathering outdoors with colorful traditional clothing and smiling faces',
  plays: '6.1K'
},
{
  id: 5,
  title: 'Anti-Poaching Tech: Drones & AI in Conservation',
  description: 'Technology expert Marcus Webb reveals how artificial intelligence and drone surveillance are changing the game in real-time poaching prevention.',
  duration: '38 min',
  date: 'Feb 7, 2026',
  series: 'Expert Series',
  topic: 'Technology',
  guestType: 'Scientist',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_10a7120cd-1767974660830.png",
  alt: 'Modern drone flying over African savanna landscape during conservation patrol mission at sunset',
  trending: true,
  plays: '11.5K'
},
{
  id: 6,
  title: 'Rhino Rehabilitation: A Second Chance',
  description: 'Veterinarian Dr. Amara Diallo walks us through the emotional journey of rehabilitating orphaned rhino calves at the Limpopo sanctuary.',
  duration: '47 min',
  date: 'Jan 30, 2026',
  series: 'Field Voices',
  topic: 'Science & Research',
  guestType: 'Veterinarian',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1d2c76e3e-1772993749408.png",
  alt: 'Veterinarian in green scrubs gently examining a young rhinoceros calf in a rehabilitation enclosure',
  plays: '8.9K'
}];


const topics = ['All Topics', 'Field Stories', 'Science & Research', 'Technology', 'Community'];
const guestTypes = ['All Guests', 'Ranger', 'Scientist', 'Veterinarian', 'Community Leader', 'Field Recorder'];
const seriesList = ['All Series', 'Field Voices', 'Expert Series', 'Field Recordings', 'Community Voices'];

export default function PodcastsInteractive() {
  const [selectedTopic, setSelectedTopic] = useState('All Topics');
  const [selectedGuest, setSelectedGuest] = useState('All Guests');
  const [selectedSeries, setSelectedSeries] = useState('All Series');
  const [searchQuery, setSearchQuery] = useState('');
  const [playingId, setPlayingId] = useState<number | null>(null);

  const filtered = episodes.filter((ep) => {
    const matchTopic = selectedTopic === 'All Topics' || ep.topic === selectedTopic;
    const matchGuest = selectedGuest === 'All Guests' || ep.guestType === selectedGuest;
    const matchSeries = selectedSeries === 'All Series' || ep.series === selectedSeries;
    const matchSearch =
    searchQuery === '' ||
    ep.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    ep.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchTopic && matchGuest && matchSeries && matchSearch;
  });

  const featuredEpisode = episodes.find((ep) => ep.featured);
  const trendingEpisodes = episodes.filter((ep) => ep.trending);

  return (
    <div className="pt-16">
      {/* Coming Soon Banner */}
      <div className="bg-primary text-primary-foreground text-center py-4">
        <span className="text-2xl font-headline font-bold tracking-wide">Coming Soon</span>
      </div>

      {/* Hero */}
      <section className="relative bg-gradient-to-br from-secondary/10 via-background to-primary/10 py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center space-x-2 px-4 py-2 bg-secondary/20 rounded-full mb-6">
              <Icon name="MicrophoneIcon" size={20} variant="solid" className="text-secondary" />
              <span className="text-sm font-body font-medium text-secondary">Conservation Audio Stories</span>
            </div>
            <h1 className="text-4xl lg:text-6xl font-headline font-bold text-foreground mb-6">
              Rhinos&Rangers Podcasts
            </h1>
            <p className="text-lg text-muted-foreground font-body mb-8">
              Immerse yourself in compelling conservation stories, expert interviews, and field recordings that deepen your emotional connection to rhino conservation.
            </p>
            {/* Search */}
            <div className="relative max-w-xl mx-auto">
              <Icon name="MagnifyingGlassIcon" size={20} variant="outline" className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search episodes, topics, guests..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-card border border-border rounded-lg text-foreground font-body placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 shadow-organic-sm" />
              
            </div>
          </div>
        </div>
      </section>

      {/* Featured Episode */}
      {featuredEpisode &&
      <section className="py-12 bg-background">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center space-x-2 mb-6">
              <Icon name="StarIcon" size={20} variant="solid" className="text-warning" />
              <h2 className="text-xl font-headline font-bold text-foreground">Featured Episode</h2>
            </div>
            <div className="bg-card rounded-xl shadow-organic border border-border overflow-hidden">
              <div className="grid lg:grid-cols-2">
                <div className="relative h-64 lg:h-auto bg-muted" />
                <div className="p-8 flex flex-col justify-center">
                  <div className="flex items-center space-x-3 mb-4">
                    <span className="px-3 py-1 bg-secondary/10 text-secondary text-xs font-body font-medium rounded-full">&nbsp;</span>
                  </div>
                  <h3 className="text-2xl font-headline font-bold text-foreground mb-3">&nbsp;</h3>
                  <p className="text-base text-muted-foreground font-body mb-6">&nbsp;</p>
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="flex items-center space-x-2 text-sm text-muted-foreground font-body">
                      <Icon name="ClockIcon" size={16} variant="outline" />
                      <span>&nbsp;</span>
                    </div>
                  </div>
                  <button className="inline-flex items-center space-x-3 px-6 py-3 bg-secondary text-secondary-foreground font-cta text-sm font-semibold rounded-md hover:bg-secondary/90 shadow-organic-sm transition-organic duration-300 w-fit opacity-0 pointer-events-none">
                    <Icon name="PlayIcon" size={20} variant="solid" />
                    <span>Play Episode</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
      }

      {/* Trending */}
      {trendingEpisodes.length > 0 &&
      <section className="py-8 bg-muted/30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center space-x-2 mb-6">
              <Icon name="FireIcon" size={20} variant="solid" className="text-warning" />
              <h2 className="text-xl font-headline font-bold text-foreground">Trending Now</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {trendingEpisodes.map((ep) =>
            <div key={ep.id} className="flex items-center space-x-4 bg-card rounded-lg p-4 border border-border shadow-organic-sm hover:shadow-organic transition-organic duration-300">
                  <div className="relative w-20 h-20 rounded-lg overflow-hidden flex-shrink-0 bg-muted" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-1">
                      <span className="px-2 py-0.5 bg-warning/10 text-warning text-xs font-body font-medium rounded-full">Trending</span>
                    </div>
                    <h4 className="text-sm font-headline font-bold text-foreground truncate">&nbsp;</h4>
                    <p className="text-xs text-muted-foreground font-body mt-1">&nbsp;</p>
                  </div>
                  <button className="p-2 bg-secondary/10 rounded-full hover:bg-secondary/20 transition-organic duration-300 flex-shrink-0 opacity-0 pointer-events-none">
                    <Icon name="PlayIcon" size={18} variant="solid" className="text-secondary" />
                  </button>
                </div>
            )}
            </div>
          </div>
        </section>
      }

      {/* Filters + All Episodes */}
      <section className="py-12 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Filters */}
          <div className="flex flex-wrap gap-3 mb-8">
            <div className="flex flex-wrap gap-2">
              {topics.map((t) =>
              <button
                key={t}
                onClick={() => setSelectedTopic(t)}
                className={`px-4 py-2 text-sm font-body font-medium rounded-full border transition-organic duration-300 ${
                selectedTopic === t ?
                'bg-primary text-primary-foreground border-primary' :
                'bg-card text-foreground border-border hover:border-primary hover:text-primary'}`
                }>
                  {t}
                </button>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {seriesList.map((s) =>
              <button
                key={s}
                onClick={() => setSelectedSeries(s)}
                className={`px-4 py-2 text-sm font-body font-medium rounded-full border transition-organic duration-300 ${
                selectedSeries === s ?
                'bg-secondary text-secondary-foreground border-secondary' :
                'bg-card text-foreground border-border hover:border-secondary hover:text-secondary'}`
                }>
                  {s}
                </button>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-headline font-bold text-foreground">All Episodes</h2>
            <span className="text-sm text-muted-foreground font-body">&nbsp;</span>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((ep) =>
            <div key={ep.id} className="bg-card rounded-xl shadow-organic-sm border border-border overflow-hidden hover:shadow-organic transition-organic duration-300 flex flex-col">
                  <div className="relative h-44 overflow-hidden bg-muted">
                    <div className="absolute top-3 left-3">
                      <span className="px-2 py-1 bg-secondary/90 text-secondary-foreground text-xs font-body font-medium rounded-full opacity-0">&nbsp;</span>
                    </div>
                    <div className="absolute bottom-3 right-3">
                      <span className="px-2 py-1 bg-foreground/60 text-white text-xs font-body rounded-full flex items-center space-x-1 opacity-0">
                        <Icon name="ClockIcon" size={12} variant="outline" />
                        <span>&nbsp;</span>
                      </span>
                    </div>
                  </div>
                  <div className="p-5 flex flex-col flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs text-muted-foreground font-body">&nbsp;</span>
                    </div>
                    <h3 className="text-base font-headline font-bold text-foreground mb-2 line-clamp-2">&nbsp;</h3>
                    <p className="text-sm text-muted-foreground font-body mb-4 flex-1 line-clamp-3">&nbsp;</p>
                    <div className="flex items-center space-x-3">
                      <button className="flex-1 inline-flex items-center justify-center space-x-2 px-4 py-2.5 font-cta text-sm font-semibold rounded-md transition-organic duration-300 bg-primary/10 text-primary hover:bg-primary hover:text-primary-foreground opacity-0 pointer-events-none">
                        <Icon name="PlayIcon" size={16} variant="solid" />
                        <span>Play</span>
                      </button>
                      <button className="p-2.5 bg-muted rounded-md hover:bg-muted/80 transition-organic duration-300 opacity-0 pointer-events-none">
                        <Icon name="BookmarkIcon" size={16} variant="outline" className="text-muted-foreground" />
                      </button>
                      <button className="p-2.5 bg-muted rounded-md hover:bg-muted/80 transition-organic duration-300 opacity-0 pointer-events-none">
                        <Icon name="ShareIcon" size={16} variant="outline" className="text-muted-foreground" />
                      </button>
                    </div>
                  </div>
                </div>
            )}
          </div>
        </div>
      </section>

      {/* Subscribe CTA */}
      <section className="py-12 bg-primary">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Icon name="MicrophoneIcon" size={48} variant="solid" className="text-warning mx-auto mb-4 opacity-0" />
          <h2 className="text-2xl lg:text-4xl font-headline font-bold text-primary-foreground mb-4 opacity-0">
            Never Miss an Episode
          </h2>
          <p className="text-lg text-primary-foreground/90 font-body mb-8 opacity-0">
            Subscribe to get notified when new conservation stories, expert interviews, and field recordings are released.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto opacity-0 pointer-events-none">
            <input
              type="email"
              placeholder="Enter your email"
              className="flex-1 px-4 py-3 bg-primary-foreground/10 border border-primary-foreground/30 rounded-md text-primary-foreground placeholder:text-primary-foreground/60 font-body focus:outline-none focus:ring-2 focus:ring-warning/50" />
            <button className="px-6 py-3 bg-warning text-warning-foreground font-cta text-sm font-semibold rounded-md hover:bg-warning/90 shadow-organic transition-organic duration-300 whitespace-nowrap">
              Subscribe
            </button>
          </div>
        </div>
      </section>
    </div>);

}